/**
 * フロー図の初期表示の縮尺と、図の面の幅。
 *
 * **縮尺は、図が枠に入る倍率を 0.75 で下止めしたもの 1 つに決める。** 倍率 1 固定で開くと、
 * 列の多いフローは節点の大半が枠の外に出る（実測: 9 中 5、23 中 20、27 中 17）。かといって
 * 枠いっぱいまで縮めると、列の多いフローでは業務名も分岐のラベルも読めない大きさになる。
 * 0.75 で止めて、それでも入らない分は横へ動かして読ませる。
 *
 * **左右比較では両面に同じ縮尺を当てる。** 面ごとに自分の幅で縮めると、同じ業務が左右で違う
 * 大きさに描かれ、並べて比べる用途が成り立たない。広いほうの図に合わせた 1 つを両面へ渡す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business フロー図の初期表示の縮尺と、左右比較で 1 面あたりに割り当てる幅を決める。
 * @graph-connects none
 */

/** 初期表示で図の左端に残す余白（実寸）。 @graph-connects none */
export const INITIAL_MARGIN = 12;

/** 縮尺の下限。これより小さくすると業務名と分岐のラベルが読めない。 @graph-connects none */
export const FIT_SCALE_FLOOR = 0.75;

/** 縮尺を決めるときに枠の幅から引く余白（左右 12px ずつ）。 @graph-connects none */
export const FIT_SCALE_INSET = 2 * INITIAL_MARGIN;

/** 左右比較で 2 面のあいだに空ける幅（px）。 @graph-connects none */
export const COMPARE_PANE_GAP = 16;

/**
 * 図が枠に入る縮尺を、1 を上限・`FIT_SCALE_FLOOR` を下限として決める。
 *
 * 幅が測れていない（0 以下）ときは実寸で返す。測れていない幅で割ると、縮尺が 0 や無限大に
 * なって図が消える。
 *
 * @param paneWidth 図 1 面の表示幅
 * @param widestCanvas 図の幅（左右比較では広いほう）
 * @returns 初期表示の縮尺
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図が枠に入る縮尺を、実寸を上限・読める大きさを下限として決める。
 * @graph-connects none
 */
export function fitScaleOf(paneWidth: number, widestCanvas: number): number {
  if (paneWidth <= 0 || widestCanvas <= 0) return 1;
  return Math.max(Math.min(1, (paneWidth - FIT_SCALE_INSET) / widestCanvas), FIT_SCALE_FLOOR);
}

/**
 * 図を収める器の幅から、図 1 面の幅を決める。
 *
 * 左右比較は 2 面を同じ幅（`1fr 1fr`）で並べるので、器の幅から隙間を引いて半分にする。
 * 器を 1 つだけ測るのは、2 面を別々に測ると同じ事実の 2 表現になるためである。
 *
 * @param frameWidth 図を収める器の幅
 * @param split 左右に並べているか
 * @returns 図 1 面の幅
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図を収める器の幅から、左右比較かどうかに応じて図 1 面の幅を決める。
 * @graph-connects none
 */
export function paneWidthOf(frameWidth: number, split: boolean): number {
  if (!split) return frameWidth;
  return Math.max(0, (frameWidth - COMPARE_PANE_GAP) / 2);
}

/**
 * 実寸で作った視点に縮尺を当てる。
 *
 * 図の面は、配置ルールごとの部品（業務フロー・業務ロジック）が作った**実寸の**視点を
 * 受け取り、枠の決めた縮尺をここで当てる。部品ごとに縮尺を掛けさせると、掛け忘れた図だけが
 * 倍率 1 のまま開く。当てるのは器の左上を原点にした拡大縮小で、実寸で左上に見えていた位置が
 * 縮めても左上に残る。
 *
 * @param viewport 実寸で作った視点
 * @param scale 当てる縮尺
 * @returns 縮尺を当てた視点
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 実寸で作った初期表示の視点に、器の左上を原点として縮尺を当てる。
 * @graph-connects none
 */
export function scaleViewportOf(
  viewport: { x: number; y: number; zoom: number },
  scale: number,
): { x: number; y: number; zoom: number } {
  return { x: viewport.x * scale, y: viewport.y * scale, zoom: viewport.zoom * scale };
}
